/**
 * Dashboard_N figures (#2139, epic #2138): every number the Month view card
 * shows, derived from one `/flows` month entry and `/tangible`. Every input
 * is the SIGNED sum `/flows` keeps (spending negative, income positive), so
 * the code adds where the page's words subtract. The words for each figure
 * are in docs/dashboard-n-figures.md and the equations in
 * docs/dashboard-equations.md; the names here follow the epic's vocabulary
 * table.
 */

const cents = (m, field) => (m && m[field]) || 0;

// One-off activity (#2210, epic #2201 Rules 1-9): lines on Pending Income,
// Planned Expenses and Planning ahead budgets, read from the copies on each
// line (#2211), leave the comparison figures for the One-off budgets row and
// still count in Net, no exclusions (Net at close) and the Raw bar.

/** Income, less repayments to a Pending Income budget (Rule 6). */
export const incomeRowCents = (m) => cents(m, 'incomeCents') - cents(m, 'oneOffIncomeCents');

/** Spending lines tagged to a Month to month budget: all spending less the untagged and one-off parts. */
export const spendingFromBudgetsCents = (m) =>
  cents(m, 'spendingCents') - cents(m, 'unbudgetedSpendingCents') - cents(m, 'oneOffSpendingCents');

/** Net outlier money: one-time out (negative) plus one-time in. */
export const outliersCents = (m) => cents(m, 'oneTimeInCents') + cents(m, 'oneTimeOutCents');

/** One-off budgets: spending and repayments on one-off budgets, netted. */
export const oneOffBudgetsCents = (m) => cents(m, 'oneOffSpendingCents') + cents(m, 'oneOffIncomeCents');

/**
 * Baseline net: Income plus both spending rows; transfers, outliers and
 * one-off budgets set aside. The chart's Baseline bar.
 */
export const baselineNetCents = (m) =>
  incomeRowCents(m) + spendingFromBudgetsCents(m) + cents(m, 'unbudgetedSpendingCents');

/**
 * Net, no exclusions ("Net at close" on a closed month): Baseline net with
 * transfers, outliers and one-off budgets counted, so every line of the
 * month. The chart's Raw bar.
 */
export const everythingCents = (m) =>
  baselineNetCents(m) + cents(m, 'transferNetCents') + outliersCents(m) + oneOffBudgetsCents(m);

/**
 * Net, all budgets spent, current month only: Income, less spending
 * outside budgets, less the budgeted funds still remaining
 * (`monthToMonthPositiveCents` in utils/budgetsByCategory).
 */
export const netIfSpentCents = (m, positiveBudgetCents) =>
  incomeRowCents(m) + cents(m, 'unbudgetedSpendingCents') - (positiveBudgetCents || 0);

/**
 * Checking, the top bar's first figure (#2206): the active Checking ledgers'
 * balances from `/tangible` `byAccount` (Checking and Credit Card rows carry
 * `type` and `archived`). Archived ledgers stay out (owner, #2139 settle
 * point 3).
 * @param {Array<{type: number, archived?: boolean, balanceCents: number}>} byAccount
 */
export const checkingCents = (byAccount) => (byAccount || [])
  .filter((l) => l.type === 0 && !l.archived)
  .reduce((sum, l) => sum + (l.balanceCents || 0), 0);

/**
 * The month switcher's list (#2139): the current month always, plus every
 * month with lines, newest first. Each carries `closed` so the dropdown can
 * say "in progress" or "closed".
 * @param {Array<{yearMonth: string, lineCount: number}>} months  oldest first
 * @param {string} currentYearMonth
 */
export const switcherMonths = (months, currentYearMonth) => (months || [])
  .filter((m) => m.yearMonth === currentYearMonth || (m.lineCount || 0) > 0)
  .map((m) => ({ yearMonth: m.yearMonth, closed: m.yearMonth !== currentYearMonth }))
  .reverse();
